// Phase 16 → 17 — /admin/contact-submissions triage.
//
// Operator triage for marketing-site Contact Us messages: filter (open/handled/
// all) + search, a detail modal with the full message + reply thread + in-app
// reply composer (sends from info@ with an editable Reply-To), mark-handled,
// delete (spam), and a Purge button for messages older than 6 months.

import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, StatusBadge, useFocusTrap } from '@truecare/ui';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';
import { AdminFilterChips } from '../components/AdminFilterChips';
import { DangerButton } from '../components/DangerButton';
import { actionErrorMessage } from '../lib/action-error';
import { adminBeforeLoad } from '../lib/admin-before-load';

export const Route = createFileRoute('/contact-submissions')({
  beforeLoad: () => adminBeforeLoad('/contact-submissions'),
  component: ContactSubmissionsPage,
});

type StatusFilter = 'open' | 'handled' | 'all';

interface ContactRow {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'NEW' | 'HANDLED';
  handledAt: string | null;
  replyCount: number;
  createdAt: string;
}
interface ContactReply {
  id: string;
  body: string;
  replyToEmail: string;
  sentByName: string | null;
  sentAt: string;
}
interface ContactDetail extends ContactRow {
  handledByName: string | null;
  replies: ContactReply[];
}

function ContactSubmissionsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState<StatusFilter>('open');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'contact-submissions'] });
  };

  const meQuery = useQuery({
    queryKey: ['admin', 'auth-me'],
    queryFn: async () => (await unwrap(api.GET('/api/v1/auth/me'))) as { email: string },
  });

  const listQuery = useQuery({
    queryKey: ['admin', 'contact-submissions', 'list', { page, pageSize, status, search }],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/contact-submissions' as never, {
          params: {
            query: {
              page: String(page),
              pageSize: String(pageSize),
              status,
              ...(search.trim() ? { search: search.trim() } : {}),
            },
          },
        } as never),
      )) as { items: ContactRow[]; total: number; page: number; pageSize: number },
  });

  const purgeableQuery = useQuery({
    queryKey: ['admin', 'contact-submissions', 'purgeable'],
    queryFn: async () =>
      (await unwrap(api.GET('/api/v1/admin/contact-submissions/purgeable' as never))) as {
        count: number;
        olderThanMonths: number;
      },
  });

  const purge = useMutation({
    mutationFn: async () =>
      unwrap(api.POST('/api/v1/admin/contact-submissions/purge' as never, {} as never)),
    onSuccess: invalidate,
  });

  const columns: AdminTableColumn<ContactRow>[] = [
    {
      key: 'createdAt',
      header: 'Received',
      width: 'w-40',
      render: (r) => new Date(r.createdAt).toLocaleString('en-CA'),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-28',
      render: (r) => <StatusBadge status={r.status} />,
    },
    { key: 'name', header: 'From', width: 'w-36' },
    { key: 'subject', header: 'Subject', width: 'w-56' },
    {
      key: 'message',
      header: 'Message',
      sortable: false,
      render: (r) => (
        <span className="text-ink-muted">
          {r.message.length > 90 ? `${r.message.slice(0, 90)}…` : r.message}
          {r.replyCount > 0 && (
            <span className="ml-2 rounded bg-forest/10 px-1.5 py-0.5 text-xs text-forest">
              {r.replyCount} repl{r.replyCount === 1 ? 'y' : 'ies'}
            </span>
          )}
        </span>
      ),
    },
  ];

  const purgeCount = purgeableQuery.data?.count ?? 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">Contact submissions</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Messages from the site's Contact Us form. Open one to read it in full,
            reply, or mark it handled.
          </p>
        </header>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <AdminFilterChips<StatusFilter>
              value={status}
              options={
                [
                  ['open', 'Open'],
                  ['handled', 'Handled'],
                  ['all', 'All'],
                ] as const
              }
              onChange={(s) => {
                setStatus(s);
                setPage(1);
              }}
              ariaLabel="Filter contact submissions by status"
              testIdPrefix="contact-filter"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search name, email, subject, message…"
              data-testid="contact-search"
              className="w-72 rounded-md border border-border bg-paper px-3 py-1.5 text-sm text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
            />
          </div>
          <DangerButton
            entityName="old contact submissions"
            actionLabel={`Purge ${purgeCount} message${purgeCount === 1 ? '' : 's'} older than ${
              purgeableQuery.data?.olderThanMonths ?? 6
            } months`}
            confirmationToken="PURGE"
            requireReason={false}
            disabled={purgeCount === 0 || purge.isPending}
            onConfirm={async () => {
              await purge.mutateAsync();
            }}
            data-testid="contact-purge"
          >
            Purge old…
          </DangerButton>
        </div>

        {purge.isError && (
          <Alert tone="danger">{actionErrorMessage(purge.error)}</Alert>
        )}

        <AdminTable
          columns={columns}
          rows={listQuery.data?.items ?? []}
          isLoading={listQuery.isPending}
          isError={listQuery.isError}
          onRetry={() => void listQuery.refetch()}
          emptyMessage="No contact submissions match this filter."
          onRowClick={(r) => setSelectedId(r.id)}
          page={page}
          limit={pageSize}
          totalCount={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
      </div>

      {selectedId && (
        <ContactDetailModal
          id={selectedId}
          defaultReplyTo={meQuery.data?.email ?? ''}
          onClose={() => setSelectedId(null)}
          onChanged={invalidate}
        />
      )}
    </AdminLayout>
  );
}

function ContactDetailModal({
  id,
  defaultReplyTo,
  onClose,
  onChanged,
}: {
  id: string;
  defaultReplyTo: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState(defaultReplyTo);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, { active: true, onEscape: onClose });
  const initialisedReplyTo = useRef(false);

  const detailQuery = useQuery({
    queryKey: ['admin', 'contact-submissions', 'detail', id],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/contact-submissions/{id}' as never, {
          params: { path: { id } },
        } as never),
      )) as ContactDetail,
  });

  // Seed Reply-To from the operator's email once it's known.
  useEffect(() => {
    if (!initialisedReplyTo.current && defaultReplyTo) {
      setReplyTo(defaultReplyTo);
      initialisedReplyTo.current = true;
    }
  }, [defaultReplyTo]);

  const refetchAll = () => {
    void detailQuery.refetch();
    onChanged();
  };

  const reply = useMutation({
    mutationFn: async () =>
      unwrap(
        api.POST('/api/v1/admin/contact-submissions/{id}/reply' as never, {
          params: { path: { id } },
          body: { body: body.trim(), replyToEmail: replyTo.trim() },
        } as never),
      ),
    onSuccess: () => {
      setBody('');
      refetchAll();
    },
  });

  const markHandled = useMutation({
    mutationFn: async () =>
      unwrap(
        api.POST('/api/v1/admin/contact-submissions/{id}/mark-handled' as never, {
          params: { path: { id } },
        } as never),
      ),
    onSuccess: refetchAll,
  });

  const remove = useMutation({
    mutationFn: async () =>
      unwrap(
        api.DELETE('/api/v1/admin/contact-submissions/{id}' as never, {
          params: { path: { id } },
        } as never),
      ),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const d = detailQuery.data;
  const emailOk = /.+@.+\..+/.test(replyTo.trim());
  const canSend = body.trim().length > 0 && emailOk && !reply.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4 pt-10">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Contact submission"
        data-testid="contact-detail-modal"
        className="w-full max-w-2xl rounded-lg border border-border bg-paper p-6 shadow-xl"
      >
        {detailQuery.isPending && <p className="text-sm text-ink-muted">Loading…</p>}
        {detailQuery.isError && (
          <Alert tone="danger">{actionErrorMessage(detailQuery.error)}</Alert>
        )}
        {d && (
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-heading text-xl text-forest">{d.subject}</h2>
                  <StatusBadge status={d.status} />
                </div>
                <p className="mt-1 text-sm text-ink-muted">
                  From <span className="text-ink">{d.name}</span> ·{' '}
                  <a className="text-forest underline" href={`mailto:${d.email}`}>
                    {d.email}
                  </a>{' '}
                  · {new Date(d.createdAt).toLocaleString('en-CA')}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded p-1 text-ink-muted hover:bg-forest/5"
              >
                ✕
              </button>
            </div>

            <div className="whitespace-pre-wrap rounded-md border border-border bg-forest/5 p-4 text-sm text-ink">
              {d.message}
            </div>

            {d.replies.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Replies
                </h3>
                {d.replies.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-md border border-l-4 border-border border-l-forest bg-paper p-3 text-sm"
                    data-testid="contact-reply-row"
                  >
                    <p className="text-xs text-ink-muted">
                      {new Date(r.sentAt).toLocaleString('en-CA')} by{' '}
                      {r.sentByName ?? 'operator'} · reply-to {r.replyToEmail}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-ink">{r.body}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply composer */}
            <div className="space-y-3 rounded-md border border-border p-4">
              <h3 className="text-sm font-semibold text-ink">Reply</h3>
              <Field label="Reply-To (where their response goes)">
                <Input
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  data-testid="contact-reply-to"
                />
              </Field>
              <Field label="Message">
                <textarea
                  rows={5}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type your reply — it's sent from info@ under your name."
                  data-testid="contact-reply-body"
                  className="block w-full resize-y rounded-md border border-border bg-paper px-3 py-2 text-sm text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
                />
              </Field>
              {reply.isError && (
                <Alert tone="danger" data-testid="contact-reply-error">
                  {actionErrorMessage(reply.error)}
                </Alert>
              )}
              <div className="flex items-center gap-3">
                <Button
                  loading={reply.isPending}
                  disabled={!canSend}
                  onClick={() => reply.mutate()}
                  data-testid="contact-reply-send"
                >
                  Send reply
                </Button>
                {d.status === 'NEW' && (
                  <Button
                    variant="ghost"
                    loading={markHandled.isPending}
                    onClick={() => markHandled.mutate()}
                    data-testid="contact-mark-handled"
                  >
                    Mark handled (no reply)
                  </Button>
                )}
                <span className="flex-1" />
                <DangerButton
                  entityName={d.subject}
                  actionLabel="Delete this contact submission"
                  confirmationToken="DELETE"
                  requireReason={false}
                  onConfirm={async () => {
                    await remove.mutateAsync();
                  }}
                  data-testid="contact-delete"
                >
                  Delete
                </DangerButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
